// ============================================================================
// TIMESHEET MASTER SCREEN
// ============================================================================
// List screen showing pending timesheet weeks. Standard ModuleHeader + SyutGrid.
// Clicking a row switches to TimesheetEntryView for that employee+week.
//
// - Default: shows all employees' weeks (role-based filtering to be added later)
// - Future: logged-in user sees own weeks; managers see reportees' weeks
// - Unsubmitted weeks appear first (NOT_STARTED > DRAFT > SUBMITTED)
// - Weeks are generated on-the-fly from system start date to current week

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutScopedEmployeeSelect,
} from 'components/ui';
import { useTimesheets } from 'hooks/api/useTimesheet';
import { employeeService } from 'api/services/employeeService';
import type { Employee, Timesheet } from 'api/types';
import {
  useTimesheetPolicy,
  dayIndexFromName,
  computeEmployeeFirstWeekStart,
} from 'hooks/api/useConfig';
import type { TimesheetSearchRequest } from 'api/types';
import TimesheetEntryView from '../entry/TimesheetEntryView';
import TimesheetReopenModal from 'components/modals/modules/timesheet/TimesheetReopenModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TimesheetMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

type ScreenView = 'list' | 'entry';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatWeekDisplay(weekStartDate: string): string {
  const [y, m, d] = weekStartDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d + 6);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${dayNames[start.getDay()]} ${String(start.getDate()).padStart(2, '0')} ${months[start.getMonth()]} - ${dayNames[end.getDay()]} ${String(end.getDate()).padStart(2, '0')} ${months[end.getMonth()]} ${end.getFullYear()}`;
}

function formatMinutesToHours(minutes: number): string {
  if (!minutes || minutes <= 0) return '\u2014';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('T')[0].split('-').map(Number);
  if (parts.length < 3) return '\u2014';
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  if (isNaN(d.getTime())) return '\u2014';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// Week start day & system implementation start now come from config (TIMESHEET_POLICY).
// See hooks/api/useConfig.ts → useTimesheetPolicy() inside the component below.

/** Format Date to YYYY-MM-DD without timezone shift */
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse YYYY-MM-DD string to local Date (no timezone issues) */
function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Snap a date back to the most recent week-start (configurable JS day-of-week index 0=Sun..6=Sat). */
function getWeekStart(date: Date, weekStartDayJs: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (d.getDay() - weekStartDayJs + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/** Generate all week start dates from startDate to endDate using the configured week start day. */
function generateWeeks(startDate: string, endDate: string, weekStartDayJs: number): string[] {
  const weeks: string[] = [];
  const start = getWeekStart(parseLocalDate(startDate), weekStartDayJs);
  const end = getWeekStart(parseLocalDate(endDate), weekStartDayJs);
  const cursor = new Date(start);
  while (cursor <= end) {
    weeks.push(toLocalDateStr(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

/** Status sort priority — unsubmitted first */
const STATUS_PRIORITY: Record<string, number> = {
  NOT_STARTED: 0,
  DRAFT: 1,
  SUBMITTED: 2,
  APPROVED: 3,
};

interface WeekRow {
  id: string;
  weekStartDate: string;
  employeeId: string;
  employeeName: string;
  statusCode: string;
  statusName: string;
  totalMinutes: number;
  submittedAt?: string | null;
  timesheetId?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TimesheetMasterScreen: React.FC<TimesheetMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  // --- View state ---
  const [view, setView] = useState<ScreenView>('list');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [selectedWeekStart, setSelectedWeekStart] = useState<string>('');

  // --- Search & filter state ---
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Active filter count — drives the count badge on the ModuleHeader filter
  // icon. Mirrors the convention used by other master screens.
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterEmployee) n++;
    if (filterStatus) n++;
    return n;
  }, [filterEmployee, filterStatus]);

  // --- Pagination state (client-side over generated weeks) ---
  const [listPage, setListPage] = useState(0);
  const [listPageSize, setListPageSize] = useState(25);

  // --- Lookups (view-scoped) ---
  // Pull employees through /Employees/visible?moduleId=timesheet-entry so the
  // generated week rows respect the caller's view ladder:
  //   view-all-employees → all firm employees
  //   view-own-team      → self + transitive reports
  //   neither            → self only
  // The Timesheets list endpoint is also scoped server-side (see TimesheetsController.ApplyScopeAsync),
  // but the screen builds week rows from the employee list — without scoping that
  // source too, the grid would show empty rows for employees the user can't open.
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setEmployeesLoading(true);
    employeeService
      .getVisible({ moduleId: 'timesheet-entry', pageSize: 500 })
      .then((res) => { if (!cancelled) setEmployees(res.data); })
      .catch(() => { if (!cancelled) setEmployees([]); })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // --- Config (TIMESHEET_POLICY) — SYSTEM scope only on the master/list view. ---
  // Per-employee week-row generation respects each employee's office override only at
  // the entry screen; here we use SYSTEM defaults for consistency across all employees.
  const { config: tsPolicy } = useTimesheetPolicy();
  const weekStartDayJs = dayIndexFromName(tsPolicy.weekStartDay);

  // Employee options for filter dropdown
  const employeeOptions = useMemo(() =>
    employees.map(e => ({
      value: e.id,
      label: `${e.name || ''}${e.code ? ` (${e.code})` : ''}`.trim() || e.id,
    })),
    [employees]
  );

  // --- Fetch existing timesheets ---
  const [tsQueryParams] = useState<TimesheetSearchRequest>({ pageSize: 200 });
  const { data: apiTimesheets, loading, refetch } = useTimesheets(
    filterEmployee ? { ...tsQueryParams, employeeId: filterEmployee } : tsQueryParams
  );

  // Refetch when filter changes
  useEffect(() => {
    refetch();
  }, [filterEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Generate week rows and merge with API data ---
  const weekRows: WeekRow[] = useMemo(() => {
    const today = toLocalDateStr(new Date());
    const timesheetMap = new Map<string, any>();
    (apiTimesheets || []).forEach((ts) => {
      const ws = (ts.weekStartDate || '').split('T')[0];
      // The list endpoint exposes totalMinutes as an aggregated SUM.
      // Fall back to summing entries (used by GetById response) when the
      // header field isn't supplied — keeps the merge resilient to either source.
      const totalMin = typeof ts.totalMinutes === 'number'
        ? ts.totalMinutes
        : (ts.entries || []).reduce((sum: number, e: any) => sum + (e.durationMinutes || 0), 0);
      timesheetMap.set(`${ts.employeeId}-${ws}`, { ...ts, totalMinutes: totalMin });
    });

    const rows: WeekRow[] = [];

    // Show all employees (for testing); role-based filtering will be added later
    const targetEmployees = filterEmployee
      ? employees.filter((e) => e.id === filterEmployee)
      : employees;

    targetEmployees.forEach((emp) => {
      const joinDate = emp.dateOfJoining ? emp.dateOfJoining.split('T')[0] : null;
      // Earliest week the employee may log time for: max(implementationStartDate, dateOfJoining)
      // snapped forward to the configured week-start day.
      const empStart = computeEmployeeFirstWeekStart(
        tsPolicy.weekStartDay,
        tsPolicy.implementationStartDate,
        joinDate
      );
      const empEnd = emp.dateLeft ? emp.dateLeft.split('T')[0] : today;
      const weeks = generateWeeks(empStart, empEnd, weekStartDayJs);

      weeks.forEach((ws) => {
        const key = `${emp.id}-${ws}`;
        const existing = timesheetMap.get(key);
        rows.push({
          id: existing?.id || key,
          weekStartDate: ws,
          employeeId: emp.id,
          employeeName: emp.name || emp.code || '',
          statusCode: existing?.statusCode || 'NOT_STARTED',
          statusName: existing?.statusName || 'Not Started',
          totalMinutes: existing?.totalMinutes || 0,
          submittedAt: existing?.submittedAt,
          timesheetId: existing?.id,
        });
      });
    });

    // Sort: unsubmitted first (NOT_STARTED > DRAFT > SUBMITTED), then by week descending
    rows.sort((a, b) => {
      const statusDiff = (STATUS_PRIORITY[a.statusCode] ?? 99) - (STATUS_PRIORITY[b.statusCode] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return b.weekStartDate.localeCompare(a.weekStartDate);
    });

    // Apply status filter
    if (filterStatus) {
      return rows.filter((r) => r.statusCode === filterStatus);
    }

    // Apply search filter (client-side text match on employee name or week display)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return rows.filter(r =>
        r.employeeName.toLowerCase().includes(q) ||
        formatWeekDisplay(r.weekStartDate).toLowerCase().includes(q) ||
        r.statusName.toLowerCase().includes(q)
      );
    }

    return rows;
  }, [apiTimesheets, employees, filterEmployee, filterStatus, searchQuery,
      tsPolicy.weekStartDay, tsPolicy.implementationStartDate, weekStartDayJs]);

  // --- Client-side pagination ---
  const pagedRows = useMemo(() => {
    const start = listPage * listPageSize;
    return weekRows.slice(start, start + listPageSize);
  }, [weekRows, listPage, listPageSize]);

  // Reset page when data changes
  useEffect(() => {
    setListPage(0);
  }, [filterEmployee, filterStatus, searchQuery]);

  // --- Column definitions ---
  const tableColumns: SyutGridColumn<WeekRow>[] = useMemo(() => {
    const cols: SyutGridColumn<WeekRow>[] = [
      {
        id: 'employeeName',
        header: 'Employee',
        accessorKey: 'employeeName',
        size: 180,
        enableSorting: true,
      },
      {
        id: 'weekStartDate',
        header: 'Week',
        accessorKey: 'weekStartDate',
        size: 260,
        enableSorting: true,
        cell: ({ row }: any) => formatWeekDisplay(row.original.weekStartDate),
      },
      {
        id: 'statusCode',
        header: 'Status',
        accessorKey: 'statusCode',
        size: 120,
        cell: ({ row }: any) => {
          const status = row.original.statusCode;
          if (status === 'SUBMITTED') return <span className="badge badge-active">Submitted</span>;
          if (status === 'APPROVED') return <span className="badge badge-active">Approved</span>;
          if (status === 'DRAFT') return <span className="badge badge-pending">Draft</span>;
          return <span className="badge badge-inactive">Not Started</span>;
        },
      },
      {
        id: 'totalHours',
        header: 'Total Hours',
        accessorKey: 'totalMinutes',
        size: 110,
        cell: ({ row }: any) => formatMinutesToHours(row.original.totalMinutes),
      },
      {
        id: 'submittedAt',
        header: 'Submitted',
        accessorKey: 'submittedAt',
        size: 140,
        cell: ({ row }: any) => formatDate(row.original.submittedAt),
      },
    ];

    return cols;
  }, []);

  // --- Handlers ---
  const handleOpenTimesheet = useCallback((row: WeekRow) => {
    setSelectedEmployeeId(row.employeeId);
    setSelectedWeekStart(row.weekStartDate);
    setView('entry');
  }, []);

  // ----- Reopen modal state -----
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState<Timesheet | null>(null);

  const handleReopenClick = useCallback((row: WeekRow) => {
    if (!row.timesheetId) return;
    // Look up the full timesheet from the API list — we need rowVersion + entries
    // for the modal's summary block.
    const ts = (apiTimesheets || []).find((t) => t.id === row.timesheetId);
    if (!ts) return;
    setReopenTarget(ts);
    setReopenModalOpen(true);
  }, [apiTimesheets]);

  // --- Row actions ---
  const getRowActions = useCallback((row: WeekRow): ActionMenuItem[] => {
    // Map timesheet status to the required permission
    const statusToAction: Record<string, string> = {
      NOT_STARTED: 'add',
      DRAFT: 'edit',
      SUBMITTED: 'view',
      APPROVED: 'view',
    };
    const requiredAction = statusToAction[row.statusCode] || 'view';
    if (!allowedActions.includes(requiredAction)) return [];

    const items: ActionMenuItem[] = [
      {
        id: 'open',
        label: row.statusCode === 'NOT_STARTED' ? 'Add Entry' : row.statusCode === 'DRAFT' ? 'Edit' : 'View',
        icon: row.statusCode === 'NOT_STARTED'
          ? <IconSystem.add size={16} />
          : row.statusCode === 'DRAFT'
            ? <IconSystem.edit size={16} />
            : <IconSystem.eye size={16} />,
        onClick: () => handleOpenTimesheet(row),
      },
    ];

    // Reopen — visible only on SUBMITTED rows when the caller has the
    // 'reopen' module action. Backend re-checks the same permission.
    if (row.statusCode === 'SUBMITTED' && row.timesheetId && allowedActions.includes('reopen')) {
      items.push({
        id: 'reopen-timesheet',
        label: 'Reopen Timesheet…',
        icon: <IconSystem.refresh size={16} />,
        onClick: () => handleReopenClick(row),
      });
    }

    return items;
  }, [allowedActions, handleOpenTimesheet, handleReopenClick]);

  const handleBackToList = useCallback(() => {
    setView('list');
    setSelectedEmployeeId('');
    setSelectedWeekStart('');
    refetch();
  }, [refetch]);

  const handleClearFilters = useCallback(() => {
    setFilterEmployee('');
    setFilterStatus('');
    setListPage(0);
  }, []);

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'new-entry',
        icon: <IconSystem.add size={16} />,
        label: 'New Timesheet',
        onClick: () => {
          setSelectedEmployeeId('');
          setSelectedWeekStart('');
          setView('entry');
        },
        variant: 'primary' as const,
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={16} />,
      label: 'Refresh',
      onClick: () => refetch(),
      variant: 'icon' as const,
    });
    return buttons;
  }, [allowedActions, refetch]);

  const statusOptions = useMemo(() => [
    { value: '', label: 'All' },
    { value: 'NOT_STARTED', label: 'Not Started' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
  ], []);

  // -------------------------------------------------------------------------
  // RENDER: Entry View
  // -------------------------------------------------------------------------

  if (view === 'entry') {
    return (
      <TimesheetEntryView
        moduleIcon={moduleIcon}
        tabId={tabId}
        moduleName={moduleName}
        preSelectedEmployeeId={selectedEmployeeId || undefined}
        preSelectedWeekStart={selectedWeekStart || undefined}
        onBackToList={handleBackToList}
        allowedActions={allowedActions}
      />
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: List View
  // -------------------------------------------------------------------------

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Timesheets"
        description="View and manage weekly timesheets for all employees."
        icon={<IconSystem.clock size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search weeks...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {(loading || employeesLoading) && weekRows.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Loading timesheets...
              </div>
            ) : (
              <SyutGrid
                data={pagedRows}
                columns={tableColumns}
                enablePagination
                manualPagination
                totalCount={weekRows.length}
                currentPage={listPage}
                pageSize={listPageSize}
                onPageChange={(p) => setListPage(p)}
                onPageSizeChange={(s) => { setListPageSize(s); setListPage(0); }}
                enableSorting
                rowActions={
                  (allowedActions.includes('add') || allowedActions.includes('edit') || allowedActions.includes('view'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage="No timesheet weeks found."
              />
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button
                className="search-panel-close"
                onClick={() => setFilterOpen(false)}
                title="Close filters"
              >
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              {/* Employee — server-side scoped to view-* on timesheet-entry */}
              <div className="filter-section">
                <SyutScopedEmployeeSelect
                  label="Employee"
                  scopeMode="view"
                  moduleId="timesheet-entry"
                  value={filterEmployee || undefined}
                  onChange={(val) => setFilterEmployee(val || '')}
                  isClearable
                  placeholder="All employees..."
                />
              </div>

              {/* Status */}
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  value={filterStatus || undefined}
                  options={statusOptions}
                  onChange={(val) => setFilterStatus((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reopen Timesheet Modal */}
      <TimesheetReopenModal
        isOpen={reopenModalOpen}
        timesheet={reopenTarget}
        onClose={() => { setReopenModalOpen(false); setReopenTarget(null); }}
        onReopened={() => { refetch(); }}
      />
    </div>
  );
};

export default TimesheetMasterScreen;
